import { useState, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import { useLenis } from 'lenis/react';
import { Logo } from '@components/layout/Logo';
import { ButtonAnchor } from '@components/ui/Button/ButtonAnchor';
import { useScrolled } from '@hooks/useScrolled';
import { ROUTES } from '@constants/routes';
import { COMPANY, whatsappUrl } from '@constants/company';
import {
  HeaderWrapper,
  HeaderInner,
  Nav,
  NavItem,
  NavCta,
  MenuButton,
  MenuLine,
  MobileDrawer,
  DrawerHeader,
  DrawerNav,
  DrawerNavItem,
  DrawerFooter,
  DrawerContact,
} from './styles';

const navLinks = [
  { label: 'Início', to: ROUTES.home },
  { label: 'Quem Somos', to: ROUTES.about },
  { label: 'Eventos', to: ROUTES.events },
];

/**
 * Header global da aplicação.
 *
 * Comportamentos:
 * - Transparente no topo, sólido (glassmorphism navy) após 80px de scroll
 * - Navegação desktop com links animados + CTA
 * - Menu mobile em drawer full-screen com AnimatePresence
 * - Fecha o drawer automaticamente ao navegar
 * - Trava o scroll quando o drawer está aberto (via Lenis)
 */
export function Header() {
  const scrolled = useScrolled(80);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lenis = useLenis();

  /* Trava/destranca o smooth scroll quando o drawer está aberto. */
  useEffect(() => {
    if (drawerOpen) {
      lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenis?.start();
      document.body.style.overflow = '';
    }

    return () => {
      lenis?.start();
      document.body.style.overflow = '';
    };
  }, [drawerOpen, lenis]);

  /* Fecha o drawer ao pressionar Escape. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const whatsappMessage = 'Olá! Gostaria de mais informações sobre os eventos da Teia de Eventos.';

  return (
    <>
      <HeaderWrapper
        $scrolled={scrolled}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      >
        <HeaderInner>
          <Logo variant="inverse" size="md" animated />

          {/* ── Navegação desktop ─────────────────── */}
          <Nav aria-label="Navegação principal">
            {navLinks.map(({ label, to }) => (
              <NavItem key={to} to={to} end={to === ROUTES.home}>
                {label}
              </NavItem>
            ))}
          </Nav>

          <NavCta>
            <ButtonAnchor
              href={whatsappUrl(whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              $variant="primary"
              $size="sm"
              whileTap={{ scale: 0.96 }}
            >
              Fale Conosco
            </ButtonAnchor>
          </NavCta>

          {/* ── Botão hamburguer mobile ─────────────────── */}
          <MenuButton
            onClick={openDrawer}
            aria-label="Abrir menu"
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
          >
            <MenuLine
              animate={drawerOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.25 }}
            />
            <MenuLine
              animate={drawerOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.2 }}
            />
            <MenuLine
              animate={drawerOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.25 }}
            />
          </MenuButton>
        </HeaderInner>
      </HeaderWrapper>

      {/* ── Drawer mobile ─────────────────────────────────── */}
      <AnimatePresence>
        {drawerOpen && (
          <MobileDrawer
            id="mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navegação"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <DrawerHeader>
              <Logo variant="inverse" size="md" />
              <MenuButton onClick={closeDrawer} aria-label="Fechar menu">
                <MenuLine animate={{ rotate: 45, y: 7 }} transition={{ duration: 0.25 }} />
                <MenuLine animate={{ opacity: 0, scaleX: 0 }} transition={{ duration: 0.2 }} />
                <MenuLine animate={{ rotate: -45, y: -7 }} transition={{ duration: 0.25 }} />
              </MenuButton>
            </DrawerHeader>

            <DrawerNav aria-label="Navegação mobile">
              {navLinks.map(({ label, to }) => (
                <DrawerNavItem
                  key={to}
                  to={to}
                  end={to === ROUTES.home}
                  onClick={closeDrawer}
                >
                  {label}
                </DrawerNavItem>
              ))}
            </DrawerNav>

            <DrawerFooter>
              <ButtonAnchor
                href={whatsappUrl(whatsappMessage)}
                target="_blank"
                rel="noopener noreferrer"
                $variant="primary"
                $size="lg"
                style={{ width: '100%', justifyContent: 'center' }}
                whileTap={{ scale: 0.96 }}
                onClick={closeDrawer}
              >
                Fale Conosco
              </ButtonAnchor>
              <DrawerContact href={`tel:${COMPANY.contact.phoneDigits}`}>
                {COMPANY.contact.phone}
              </DrawerContact>
              <DrawerContact href={`mailto:${COMPANY.contact.email}`}>
                {COMPANY.contact.email}
              </DrawerContact>
            </DrawerFooter>
          </MobileDrawer>
        )}
      </AnimatePresence>
    </>
  );
}
